import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Input } from 'reactstrap';
// Round DMS-UAT-1 (2026) — Issue 7: Product Info is now edited as an
// ordered, one-row-per-part grid instead of six lumped free-text fields.
//
// STORAGE IS UNCHANGED (zero schema change): the parent form still keeps
// the six legacy columns (partDescription, partNumber, drawingNumber(s),
// customerPartNumber, currentRev, newRev) as plain strings. This tab parses
// them into rows for editing (line N of each column = one part line —
// exactly the legacy DMSNew multiline convention) and serializes every edit
// straight back into the same six newline-joined strings via the existing
// update(field, value) callback, so drafts, the submit payload, the backend
// columns and the PDF all keep working on the same data shape.
import { toast } from "react-toastify";
import {
    parseProductInfoRows,
    serializeProductInfoRows,
    applyPastedMatrix,
    blankProductInfoRow,
    PRODUCT_INFO_FIELDS,
} from '../../utils/productInfoRows';
// Round DMS-IssueRound-1 (2026) — Issue 3: Excel multi-row / multi-column
// paste. The clipboard parser is shared with the rich-text editor.
import { matrixFromClipboardData, isSingleCell, nonEmptyCellCount } from '../../utils/clipboardTable';

// The parent form model calls the drawing field "drawingNumbers" (plural)
// while the parser/storage use "drawingNumber" — map on the way in/out.
const fromParent = (data) => ({
    partDescription: data?.partDescription,
    partNumber: data?.partNumber,
    drawingNumber: data?.drawingNumbers ?? data?.drawingNumber,
    customerPartNumber: data?.customerPartNumber,
    currentRev: data?.currentRev,
    newRev: data?.newRev,
});

const rowsSignature = (rows) => JSON.stringify(serializeProductInfoRows(rows));

const EcnProductInfoTab = ({ data, update }) => {

    // Rows the user is editing. Blank rows (freshly added, not yet typed
    // into) live only here — serialization drops fully-empty rows, so
    // nothing empty is ever written into the stored fields.
    const [localRows, setLocalRows] = useState(() => parseProductInfoRows(fromParent(data)));

    // Resync from the parent when its stored strings change EXTERNALLY
    // (e.g. resume-draft load) — but not when the change is the echo of our
    // own commit, which would clobber a blank row mid-entry.
    const parentSignature = useMemo(() => rowsSignature(parseProductInfoRows(fromParent(data))), [data]);
    useEffect(() => {
        if (parentSignature !== rowsSignature(localRows)) {
            setLocalRows(parseProductInfoRows(fromParent(data)));
        }
        
    }, [parentSignature]);

    // Push the current rows back into the parent's six string fields.
    const commitRows = (nextRows) => {
        const serialized = serializeProductInfoRows(nextRows);
        update("partDescription", serialized.partDescription);
        update("partNumber", serialized.partNumber);
        update("drawingNumbers", serialized.drawingNumber);
        update("customerPartNumber", serialized.customerPartNumber);
        update("currentRev", serialized.currentRev);
        update("newRev", serialized.newRev);
    };

    const editCell = (rowIdx, key, value) => {
        const next = localRows.map((r, i) => (i === rowIdx ? { ...r, [key]: value } : r));
        setLocalRows(next);
        commitRows(next);
    };

    const addRow = () => {
        setLocalRows((prev) => [...prev, blankProductInfoRow()]);
        // no commit yet — an empty row has nothing to store
    };

    // Round DMS-IssueRound-1 (2026) — Issue 3: paste an Excel range.
    // Starts at the focused cell (row, column), fills across the six
    // columns and DOWN the rows, creating rows as needed. A single copied
    // value keeps the browser's normal paste into the one input. Values
    // that would fall beyond the last column are dropped and reported —
    // never shifted into another column.
    const handleCellPaste = (e, rowIdx, colIdx) => {
        let matrix;
        try {
            // allowSingleColumn: a copied Excel COLUMN has no tabs, only lines.
            matrix = matrixFromClipboardData(e.clipboardData, { allowSingleColumn: true });
        } catch {
            matrix = null;
        }
        if (!matrix || isSingleCell(matrix) || nonEmptyCellCount(matrix) === 0) return;
        e.preventDefault();
        const { rows, applied, droppedCells, createdRows } =
            applyPastedMatrix(localRows, matrix, rowIdx, colIdx);
        setLocalRows(rows);
        commitRows(rows);
        const summary = `Pasted ${matrix.rows.length} row${matrix.rows.length === 1 ? "" : "s"}`
            + ` (${applied} cell${applied === 1 ? "" : "s"})`
            + (createdRows > 0 ? `, ${createdRows} product line${createdRows === 1 ? "" : "s"} added` : "")
            + ".";
        if (droppedCells > 0) {
            toast.warn(`${summary} ${droppedCells} value${droppedCells === 1 ? "" : "s"} beyond "New Rev" `
                + `were ignored — start the paste in an earlier column to include them.`);
        } else {
            toast.success(summary);
        }
    };

    const deleteRow = (rowIdx) => {
        const row = localRows[rowIdx];
        const label = row?.partNumber || row?.partDescription || `line ${rowIdx + 1}`;
        // Destructive action confirms with Cancel (same rule as task delete).
        if (!window.confirm(`Remove product line "${label}"?`)) return;
        const next = localRows.filter((_, i) => i !== rowIdx);
        setLocalRows(next);
        commitRows(next);
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-muted small">
                    One row per part. Rows keep their entry order; use the trash
                    icon to remove a line. Tip: copy a range of cells in Excel and
                    paste into any cell — the rows below are filled in automatically.
                </div>
                <Button color="primary" size="sm" outline onClick={addRow}>
                    <i className="ri-add-line align-bottom me-1"></i>Add product line
                </Button>
            </div>

            <Table bordered responsive size="sm" className="align-middle">
                <thead className="table-light">
                    <tr>
                        <th style={{ width: 36 }}>#</th>
                        {PRODUCT_INFO_FIELDS.map((f) => (
                            <th key={f.key}>{f.label}</th>
                        ))}
                        <th style={{ width: 48 }}></th>
                    </tr>
                </thead>
                <tbody>
                    {localRows.length === 0 ? (
                        <tr>
                            <td colSpan={PRODUCT_INFO_FIELDS.length + 2} className="text-muted text-center py-4">
                                No product lines yet — click "Add product line" to start.
                            </td>
                        </tr>
                    ) : (
                        localRows.map((row, i) => (
                            <tr key={i}>
                                <td className="text-muted">{i + 1}</td>
                                {PRODUCT_INFO_FIELDS.map((f, colIdx) => (
                                    <td key={f.key}>
                                        <Input
                                            bsSize="sm"
                                            value={row[f.key] || ""}
                                            onChange={(e) => editCell(i, f.key, e.target.value)}
                                            onPaste={(e) => handleCellPaste(e, i, colIdx)}
                                            title="Paste an Excel range here: it fills across the columns and down the rows from this cell"
                                        />
                                    </td>
                                ))}
                                <td className="text-center">
                                    <Button
                                        color="danger"
                                        size="sm"
                                        outline
                                        title="Remove this product line"
                                        onClick={() => deleteRow(i)}
                                    >
                                        <i className="ri-delete-bin-line"></i>
                                    </Button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
        </>
    );
};


export default EcnProductInfoTab;
